import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { ChangePasswordCurrentUserPayload } from '@/features/auth/types/login';
import { HttpClient, buildEndpointUrl } from '@/libs/axios';
import { MutateDefaultOptions } from '@/models/api';
import { getAxiosBackendError } from '@/utils/errors';

export const getChangePasswordCurrentUserMutateKey = () => ['changePasswordCurrentUser'];

export const getChangePasswordCurrentUserEndpoint = () =>
    buildEndpointUrl('USERS', { additionalPaths: ['change-password'] });

export const apiChangePasswordCurrentUser = (payload: ChangePasswordCurrentUserPayload) =>
    HttpClient.patch<void>(getChangePasswordCurrentUserEndpoint(), payload);

//

export const useChangePasswordCurrentUser = () => {
    const { mutateAsync, isPending: isChangingPassword } = useMutation<
        unknown,
        AxiosError,
        ChangePasswordCurrentUserPayload
    >({
        mutationKey: getChangePasswordCurrentUserMutateKey(),
        mutationFn: (payload) => apiChangePasswordCurrentUser(payload),
    });

    const changePasswordCurrentUser = useCallback(
        async (
            payload: ChangePasswordCurrentUserPayload,
            { onSuccess, onError, onSettled }: MutateDefaultOptions = {},
        ) => {
            try {
                await mutateAsync(payload);
            } catch (error: unknown) {
                await onError?.(getAxiosBackendError(error, true));
                await onSettled?.();
                return;
            }

            await onSuccess?.();
            await onSettled?.();
        },
        [mutateAsync],
    );

    return { changePasswordCurrentUser, isChangingPassword };
};
